import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Mail, Lock, User } from "lucide-react";
import { toast } from "sonner";
import { api, setToken, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const HERO = "https://images.unsplash.com/photo-1513267000941-598abe7be16f?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80";

export default function Auth({ onAuthed }) {
  const [mode, setMode] = useState("login"); // login | register
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!email || !password) return toast.error("Please enter your email and password.");
    setBusy(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const body = mode === "login" ? { email, password } : { email, password, name };
      const { data } = await api.post(path, body);
      setToken(data.token);
      toast.success(mode === "login" ? `Welcome back, ${data.name || "love"} 💕` : "Account created 🎉");
      onAuthed(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <img src={HERO} alt="" className="w-full h-72 object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/85 to-background" />
        <div className="absolute inset-0 grain opacity-[0.15]" />
      </div>

      <div className="max-w-md mx-auto px-5 pt-16 pb-10">
        <div className="flex items-center gap-2 mb-8">
          <div className="h-9 w-9 rounded-full bg-primary flex items-center justify-center shadow-lg pulse-glow">
            <Heart className="h-5 w-5 text-primary-foreground fill-primary-foreground" />
          </div>
          <span className="text-xs uppercase tracking-[0.25em] font-bold text-primary">Long Distance Us</span>
        </div>

        <h1 className="font-serif text-4xl font-semibold leading-tight">
          {mode === "login" ? "Welcome back" : "Create your account"}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {mode === "login" ? "Sign in to reach your shared space." : "One account, connected on every device."}
        </p>

        <div className="mt-7 space-y-4 rounded-3xl bg-card border border-border/70 p-5 shadow-sm">
          <AnimatePresence>
            {mode === "register" && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="space-y-1.5 overflow-hidden">
                <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Your name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="rounded-xl h-11 pl-9" />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input data-testid="auth-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="rounded-xl h-11 pl-9" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                data-testid="auth-password-input" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submit()}
                placeholder="••••••••" className="rounded-xl h-11 pl-9"
              />
            </div>
          </div>
          <Button data-testid="auth-submit-btn" disabled={busy} onClick={submit} className="w-full rounded-full h-12 font-semibold">
            {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
          </Button>
        </div>

        <button
          data-testid="auth-toggle-btn"
          onClick={() => setMode(mode === "login" ? "register" : "login")}
          className="mt-5 w-full text-center text-sm text-muted-foreground"
        >
          {mode === "login" ? (
            <>New here? <span className="text-primary font-semibold">Create an account</span></>
          ) : (
            <>Already have an account? <span className="text-primary font-semibold">Sign in</span></>
          )}
        </button>
      </div>
    </div>
  );
}
